import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { BarChart } from '../../shared/charts/bar-chart/bar-chart';
import { LineChart } from '../../shared/charts/line-chart/line-chart';
import { StackedBarChart } from '../../shared/charts/stacked-bar-chart/stacked-bar-chart';
import type { ContextDump, Metrics } from '../../core/models/metrics';
import { MetricsService } from '../../core/services/metrics.service';
import { LivekitService } from '../../core/services/livekit.service';

@Component({
  selector: 'app-metrics',
  imports: [LineChart, BarChart, StackedBarChart, DecimalPipe],
  templateUrl: './metrics.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetricsPage implements OnInit {
  private readonly service = inject(MetricsService);
  private readonly livekit = inject(LivekitService);
  private readonly destroyRef = inject(DestroyRef);

  readonly metrics = signal<Metrics[]>([]);
  readonly isExporting = signal(false);
  readonly contextUnavailable = signal(false);

  /** Freshness label derived from the last export result. */
  readonly contextFreshnessLabel = signal<string | null>(null);

  /**
   * The room to display: prefer the real-time snapshot pushed over the websocket,
   * falling back to the HTTP-loaded list for the first paint / when no live data
   * has arrived yet. All downstream stats & charts derive from `room()`.
   */
  readonly room = computed(() => this.livekit.liveMetrics() ?? this.metrics()[0]);

  constructor() {
    // Leave the room (and clear the live snapshot) when navigating away.
    this.destroyRef.onDestroy(() => this.livekit.cleanupAfterDisconnection().subscribe());
  }

  readonly totalTokens = computed(() => {
    const room = this.room();
    if (!room) return 0;
    return room.totals.promptTokens + room.totals.completionTokens;
  });

  readonly avgPerCall = computed(() => {
    const room = this.room();
    if (!room || !room.totals.calls) return 0;
    return Math.round(this.totalTokens() / room.totals.calls);
  });

  readonly ratio = computed(() => {
    const room = this.room();
    if (!room || !room.totals.completionTokens) return '—';
    return (room.totals.promptTokens / room.totals.completionTokens).toFixed(2) + '×';
  });

  readonly promptPercent = computed(() => {
    const total = this.totalTokens();
    if (!total) return '0.0';
    return (((this.room()?.totals.promptTokens ?? 0) / total) * 100).toFixed(1);
  });

  readonly completionPercent = computed(() => {
    const total = this.totalTokens();
    if (!total) return '0.0';
    return (((this.room()?.totals.completionTokens ?? 0) / total) * 100).toFixed(1);
  });

  readonly dominantSource = computed(() => {
    const bySource = this.room()?.bySource;
    if (!bySource) return '—';
    let max = 0;
    let dominant = '—';
    for (const [source, totals] of Object.entries(bySource)) {
      const total = totals.promptTokens + totals.completionTokens;
      if (total > max) {
        max = total;
        dominant = source;
      }
    }
    return dominant;
  });

  readonly lineSeriesA = computed(() =>
    this.room()?.entries.filter((e) => e.source === 'chat').map((e) => e.promptTokens) ?? [],
  );
  readonly lineSeriesB = computed(() =>
    this.room()?.entries.filter((e) => e.source === 'chat').map((e) => e.completionTokens) ?? [],
  );

  readonly barLabels = computed(() => Object.keys(this.room()?.bySource ?? {}));
  readonly barSeriesA = computed(() =>
    this.barLabels().map((l) => this.room()?.bySource[l].promptTokens ?? 0),
  );
  readonly barSeriesB = computed(() =>
    this.barLabels().map((l) => this.room()?.bySource[l].completionTokens ?? 0),
  );

  // ── Compression signals ────────────────────────────────────────────────────

  private readonly compressionEvents = computed(() =>
    [...(this.room()?.compressionEvents ?? [])].sort((a, b) => a.timestamp - b.timestamp),
  );

  private readonly transcriptBatches = computed(() =>
    this.compressionEvents().filter((e) => e.source === 'transcript'),
  );

  private readonly visionBatches = computed(() =>
    this.compressionEvents().filter((e) => e.source === 'vision'),
  );

  readonly hasCompression = computed(() => this.compressionEvents().length > 0);

  // Stat cards
  readonly totalCharsSaved = computed(() =>
    this.compressionEvents().reduce((s, e) => s + e.rawChars - e.compressedChars, 0),
  );

  readonly avgTranscriptRatio = computed(() => {
    const b = this.transcriptBatches();
    if (!b.length) return '—';
    return (
      Math.round(b.reduce((s, e) => s + (1 - e.compressedChars / e.rawChars) * 100, 0) / b.length) + '%'
    );
  });

  readonly avgVisionRatio = computed(() => {
    const b = this.visionBatches();
    if (!b.length) return '—';
    return (
      Math.round(b.reduce((s, e) => s + (1 - e.compressedChars / e.rawChars) * 100, 0) / b.length) + '%'
    );
  });

  // Transcript raw vs compressed bar chart — in tokens
  readonly transcriptBatchLabels = computed(() =>
    this.transcriptBatches().map((_, i) => `#${i + 1}`),
  );
  readonly transcriptRawSeries = computed(() =>
    this.transcriptBatches().map((e) => Math.round(e.rawChars / 4)),
  );
  readonly transcriptCompressedSeries = computed(() =>
    this.transcriptBatches().map((e) => e.completionTokens),
  );

  // Vision raw vs compressed bar chart — in tokens
  readonly visionBatchLabels = computed(() =>
    this.visionBatches().map((_, i) => `#${i + 1}`),
  );
  readonly visionRawSeries = computed(() =>
    this.visionBatches().map((e) => Math.round(e.rawChars / 4)),
  );
  readonly visionCompressedSeries = computed(() =>
    this.visionBatches().map((e) => e.completionTokens),
  );

  // Total context size in tokens at each compression event
  readonly totalContextTokensSeries = computed(() =>
    this.compressionEvents().map((e) => Math.round(e.totalContextChars / 4)),
  );

  // Context composition — 100% stacked proportion per compression event
  readonly proportionLabels = computed(() =>
    this.compressionEvents().map((_, i) => `#${i + 1}`),
  );

  readonly proportionSeries = computed(() => {
    const config = [
      { label: 'Chat',                  color: '#7c9cff', key: 'chatChars' },
      { label: 'Raw transcript',        color: '#ffb86b', key: 'rawTranscriptChars' },
      { label: 'Raw vision',            color: '#58e1c1', key: 'rawVisionChars' },
      { label: 'Compressed transcript', color: '#f97fb7', key: 'compressedTranscriptChars' },
      { label: 'Compressed vision',     color: '#a78bfa', key: 'compressedVisionChars' },
    ] as const;

    return config.map(({ label, color, key }) => ({
      label,
      color,
      data: this.compressionEvents().map((e) => {
        const s = e.contextSnapshot;
        if (!s) return 0;
        const total =
          s.chatChars + s.rawTranscriptChars + s.rawVisionChars +
          s.compressedTranscriptChars + s.compressedVisionChars;
        return total ? Math.round((s[key] / total) * 100) : 0;
      }),
    }));
  });

  ngOnInit(): void {
    // First paint / fallback: load the current metrics over HTTP.
    this.service.getMetrics().subscribe((metrics) => this.metrics.set(metrics));

    // Join the room as a metrics viewer to receive real-time snapshots over the
    // websocket. `autoSubscribe: false` keeps us from pulling any media tracks.
    this.livekit
      .connect({ role: 'metrics', participantName: 'metrics-viewer' }, { autoSubscribe: false })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // If the live channel is unavailable (e.g. backend without the metrics
        // role yet), the page still works through the HTTP fallback above.
        error: (err) => console.warn('Live metrics unavailable, using HTTP only.', err),
      });
  }

  exportContext(): void {
    const room = this.room();
    if (!room || this.isExporting()) return;

    this.isExporting.set(true);
    this.contextUnavailable.set(false);
    this.contextFreshnessLabel.set(null);

    this.service.getContext(room.roomName).subscribe({
      next: (dump: ContextDump) => {
        this.isExporting.set(false);
        const ageSeconds = Math.round((Date.now() - dump.updatedAt) / 1000);
        this.contextFreshnessLabel.set(`Context snapshot from ${ageSeconds}s ago`);
        this.triggerDownload(dump);
      },
      error: (err: HttpErrorResponse) => {
        this.isExporting.set(false);
        if (err.status === 404) {
          this.contextUnavailable.set(true);
        }
      },
    });
  }

  private triggerDownload(dump: ContextDump): void {
    const iso = new Date().toISOString().replace(/[:.]/g, '-');
    const fileName = `context-${dump.roomName}-${iso}.md`;
    const blob = new Blob([dump.promptString], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }
}
